import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { analyticsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, User, Calendar, Moon, Euro } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, parseISO, differenceInDays, isSameDay, startOfWeek, endOfWeek, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';

const PLATFORM_COLORS = {
  airbnb: { bg: 'bg-rose-500', text: 'text-white', light: 'bg-rose-500/20' },
  booking: { bg: 'bg-blue-500', text: 'text-white', light: 'bg-blue-500/20' },
  direct: { bg: 'bg-primary', text: 'text-primary-foreground', light: 'bg-primary/20' },
};

export default function CalendarPage() {
  const { selectedProperty } = useOutletContext();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedProperty, currentMonth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = selectedProperty !== 'all' ? { property_id: selectedProperty } : {};
      const [eventsRes, summaryRes] = await Promise.all([
        analyticsApi.getCalendarEvents(params),
        analyticsApi.getSummary(params),
      ]);
      setEvents(eventsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Calendar grid setup - 6 weeks view
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Weekday headers
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  // Process events into block positions
  const getEventBlocks = () => {
    const blocks = [];
    
    events.forEach((event) => {
      const startDate = parseISO(event.start);
      const endDate = parseISO(event.end);
      
      // Find start and end positions in the calendar grid
      const startIndex = days.findIndex(d => isSameDay(d, startDate));
      const endIndex = days.findIndex(d => isSameDay(d, endDate));
      
      if (startIndex === -1 && endIndex === -1) {
        // Event is outside visible range but might span through it
        const firstDayIndex = days.findIndex(d => d >= startDate && d <= endDate);
        if (firstDayIndex !== -1) {
          // Event spans into this view
          const lastDayIndex = days.findIndex(d => d >= endDate) - 1;
          blocks.push({
            ...event,
            startIndex: Math.max(0, firstDayIndex),
            endIndex: lastDayIndex >= 0 ? lastDayIndex : days.length - 1,
            isPartial: true
          });
        }
        return;
      }
      
      // Split into weekly rows
      let currentStart = startIndex >= 0 ? startIndex : 0;
      const actualEnd = endIndex >= 0 ? endIndex : days.length - 1;
      
      while (currentStart <= actualEnd) {
        const rowStart = Math.floor(currentStart / 7) * 7;
        const rowEnd = rowStart + 6;
        const blockEnd = Math.min(actualEnd, rowEnd);
        
        blocks.push({
          ...event,
          startIndex: currentStart,
          endIndex: blockEnd,
          row: Math.floor(currentStart / 7),
          colStart: currentStart % 7,
          colSpan: blockEnd - currentStart + 1,
          isStart: currentStart === startIndex || currentStart === 0,
          isEnd: blockEnd === actualEnd,
          price: event.total_price || event.net_revenue || 0,
        });
        
        currentStart = rowEnd + 1;
      }
    });
    
    return blocks;
  };

  const eventBlocks = getEventBlocks();

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setDetailsOpen(true);
  };

  const getEventColor = (platform) => PLATFORM_COLORS[platform] || PLATFORM_COLORS.direct;

  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold tracking-tight">Calendrier</h1>
          <p className="text-muted-foreground text-sm hidden sm:block">Vue de vos réservations</p>
        </div>
        
        {/* Quick Stats - Mobile optimized */}
        <div className="flex gap-2">
          <Card className="bg-card border-border/40 px-3 py-1.5 flex-1 sm:flex-none">
            <div className="text-[10px] text-muted-foreground uppercase">Ce mois</div>
            <div className="text-base md:text-lg font-semibold text-primary tabular-nums">€{(summary?.month_revenue || 0).toLocaleString()}</div>
          </Card>
          <Card className="bg-card border-border/40 px-3 py-1.5 flex-1 sm:flex-none">
            <div className="text-[10px] text-muted-foreground uppercase">Réservations</div>
            <div className="text-base md:text-lg font-semibold tabular-nums">{summary?.total_reservations || 0}</div>
          </Card>
        </div>
      </div>

      {/* Calendar */}
      <Card className="bg-card border-border/40 overflow-hidden">
        {/* Month Navigation */}
        <CardHeader className="pb-2 px-3 md:px-6">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCurrentMonth(prev => subMonths(prev, 1));
              }}
              className="h-9 w-9 touch-manipulation"
              data-testid="prev-month-btn"
              type="button"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <CardTitle className="font-heading text-base md:text-lg capitalize" data-testid="current-month">
              {format(currentMonth, 'MMMM yyyy', { locale: fr })}
            </CardTitle>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCurrentMonth(prev => addMonths(prev, 1));
              }}
              className="h-9 w-9 touch-manipulation"
              data-testid="next-month-btn"
              type="button"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-2 md:p-4">
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 mb-1">
            {weekDays.map((day) => (
              <div key={day} className="text-center text-[10px] md:text-xs text-muted-foreground py-1 md:py-2 font-medium">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid with Event Blocks */}
          <div className="relative">
            {/* Day cells */}
            <div className="grid grid-cols-7 gap-px bg-border/30">
              {days.map((day, index) => {
                const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                const isToday = isSameDay(day, new Date());
                
                return (
                  <div
                    key={day.toISOString()}
                    className={`min-h-[60px] md:min-h-[80px] lg:min-h-[100px] p-1 bg-card relative ${
                      !isCurrentMonth ? 'opacity-40' : ''
                    } ${isToday ? 'ring-1 ring-primary ring-inset' : ''}`}
                  >
                    <div className={`text-[11px] md:text-xs font-medium ${
                      isToday ? 'text-primary font-bold' : 'text-muted-foreground'
                    }`}>
                      {format(day, 'd')}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Event Blocks Overlay - Airbnb Style */}
            <div className="absolute inset-0 pointer-events-none">
              {eventBlocks.map((block, blockIndex) => {
                const colors = getEventColor(block.platform);
                const topOffset = block.row * (60 + 1) + 20; // Adjust for day number
                const leftPercent = (block.colStart / 7) * 100;
                const widthPercent = (block.colSpan / 7) * 100;
                
                return (
                  <div
                    key={`${block.id}-${blockIndex}`}
                    className={`absolute pointer-events-auto cursor-pointer transition-all hover:brightness-110 active:scale-[0.98] touch-manipulation ${colors.bg} ${colors.text}`}
                    style={{
                      top: `${topOffset}px`,
                      left: `calc(${leftPercent}% + 2px)`,
                      width: `calc(${widthPercent}% - 4px)`,
                      height: '36px',
                      borderRadius: block.isStart && block.isEnd ? '6px' : 
                                    block.isStart ? '6px 0 0 6px' : 
                                    block.isEnd ? '0 6px 6px 0' : '0',
                      zIndex: 10,
                    }}
                    onClick={() => handleEventClick(block)}
                    data-testid={`event-block-${block.id}`}
                  >
                    <div className="px-2 py-0.5 h-full flex flex-col justify-center overflow-hidden">
                      <span className="text-[10px] md:text-xs font-medium truncate leading-tight">
                        {block.isStart ? block.title : ''}
                      </span>
                      {block.isStart && block.price > 0 && (
                        <span className="text-[9px] md:text-[10px] opacity-90 truncate leading-tight">
                          {block.price.toFixed(0)}€
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Mobile-optimized: adjust heights */}
            <style>{`
              @media (min-width: 768px) {
                .absolute.pointer-events-auto {
                  height: 40px !important;
                }
              }
              @media (min-width: 1024px) {
                .absolute.pointer-events-auto {
                  height: 44px !important;
                }
              }
            `}</style>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 md:gap-4 mt-3 md:mt-4 pt-3 md:pt-4 border-t border-border/40">
            <div className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded-sm ${PLATFORM_COLORS.airbnb.bg}`} />
              <span className="text-[11px] md:text-xs text-muted-foreground">Airbnb</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded-sm ${PLATFORM_COLORS.booking.bg}`} />
              <span className="text-[11px] md:text-xs text-muted-foreground">Booking</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded-sm ${PLATFORM_COLORS.direct.bg}`} />
              <span className="text-[11px] md:text-xs text-muted-foreground">Direct</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Reservations - Mobile Card List */}
      <Card className="bg-card border-border/40">
        <CardHeader className="pb-2 px-4">
          <CardTitle className="font-heading text-base md:text-lg">Prochaines réservations</CardTitle>
        </CardHeader>
        <CardContent className="px-2 md:px-4">
          {loading ? (
            <div className="text-center py-6 text-muted-foreground text-sm">Chargement...</div>
          ) : (summary?.upcoming_reservations || []).length === 0 ? (
            <div className="text-center py-6 text-muted-foreground text-sm">Aucune réservation à venir</div>
          ) : (
            <div className="space-y-2">
              {(summary?.upcoming_reservations || []).map((res) => {
                const colors = getEventColor(res.platform);
                return (
                  <div
                    key={res.id}
                    className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 active:bg-muted/60 transition-colors touch-manipulation"
                    onClick={() => { setSelectedEvent(res); setDetailsOpen(true); }}
                  >
                    <div className={`w-1.5 h-12 rounded-full ${colors.bg}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{res.guest_name || 'Réservation'}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(parseISO(res.check_in), 'd MMM', { locale: fr })} → {format(parseISO(res.check_out), 'd MMM', { locale: fr })}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-sm tabular-nums">{res.nights}n</p>
                      <Badge variant="outline" className="text-[10px] px-1.5 capitalize">{res.platform}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reservation Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-sm mx-4 bg-card border-border rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Détails réservation</DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getEventColor(selectedEvent.platform).light}`}>
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium">{selectedEvent.title || selectedEvent.guest_name || 'Réservation'}</p>
                  <Badge className={`${getEventColor(selectedEvent.platform).bg} ${getEventColor(selectedEvent.platform).text} text-xs`}>
                    {selectedEvent.platform}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Calendar className="h-4 w-4" />
                    <span className="text-xs">Arrivée</span>
                  </div>
                  <p className="font-medium text-sm">
                    {format(parseISO(selectedEvent.start || selectedEvent.check_in), 'd MMMM yyyy', { locale: fr })}
                  </p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Calendar className="h-4 w-4" />
                    <span className="text-xs">Départ</span>
                  </div>
                  <p className="font-medium text-sm">
                    {format(parseISO(selectedEvent.end || selectedEvent.check_out), 'd MMMM yyyy', { locale: fr })}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Moon className="h-4 w-4" />
                    <span className="text-xs">Nuits</span>
                  </div>
                  <p className="font-semibold text-lg">{selectedEvent.nights || 0}</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Euro className="h-4 w-4" />
                    <span className="text-xs">Revenu</span>
                  </div>
                  <p className="font-semibold text-lg text-primary">
                    €{(selectedEvent.net_revenue || 0).toLocaleString()}
                  </p>
                </div>
              </div>

              <Button 
                variant="secondary" 
                className="w-full touch-manipulation" 
                onClick={() => setDetailsOpen(false)}
              >
                Fermer
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
